import { memo } from "react";
import { useTranslation } from "react-i18next";

import { Button, DocumentCallout, MilestoneRail, Reveal, SectionHeading, Seo } from "@/components";
import { Container } from "@/layouts";
import { PROJECT_TIMELINE } from "@/data";
import { ROUTES, normalizeLanguage, useLocalePath } from "@/navigations";
import { breadcrumbJsonLd } from "@/utils/seo";

import shared from "@/styles/pages.module.css";
import styles from "./styles.module.css";

const PHASE_KEYS = ["phase1", "phase2", "phase3"] as const;

const AboutPage = memo(() => {
  const { t } = useTranslation("AboutPage");
  const { t: tSeo } = useTranslation("Seo");
  const { t: tc, i18n } = useTranslation("components");
  const lang = normalizeLanguage(i18n.language);
  const lp = useLocalePath();
  const principles = t("principles.items", { returnObjects: true }) as { title: string; body: string }[];
  const objectives = t("objectives.items", { returnObjects: true }) as { title: string; body: string }[];
  const systemItems = t("system.items", { returnObjects: true }) as { title: string; body: string }[];
  const outOfScope = t("outOfScope.items", { returnObjects: true }) as string[];
  const milestones = PROJECT_TIMELINE.map((item) => ({
    id: item.id,
    label: t(`timeline.items.${item.id}.label`),
    title: t(`timeline.items.${item.id}.title`),
    body: t(`timeline.items.${item.id}.body`),
  }));

  return (
    <>
      <Seo
        title={tSeo("about.title")}
        description={tSeo("about.description")}
        path={ROUTES.ABOUT}
        jsonLd={breadcrumbJsonLd(
          [
            { name: tc("nav.home"), path: ROUTES.HOME },
            { name: tc("nav.project"), path: ROUTES.ABOUT },
          ],
          lang,
        )}
      />

      <section className={shared.pageHeader}>
        <Container>
          <Reveal className={shared.pageHeaderInner}>
            <span className="eyebrow" data-reveal>
              {t("hero.eyebrow")}
            </span>
            <h1 className={shared.pageTitle} data-reveal>
              {t("hero.title")}
            </h1>
            <p className={shared.lede} data-reveal>
              {t("hero.lead")}
            </p>
          </Reveal>
        </Container>
      </section>

      <section className={`${shared.section} ${shared.fieldbook}`}>
        <Container>
          <Reveal className={shared.sourceGrid}>
            <div>
              <SectionHeading title={t("purpose.title")} lead={t("purpose.body")} />
            </div>
            <DocumentCallout source="SPF" title={t("scope.sourceTitle")}>
              {t("scope.sourceBody")}
            </DocumentCallout>
          </Reveal>
        </Container>
      </section>

      <section className={shared.section}>
        <Container>
          <SectionHeading title={t("phases.title")} />
          <Reveal className={`${styles.phaseRail} ${shared.stackGap}`}>
            {PHASE_KEYS.map((key) => (
              <article key={key} className={styles.phaseCard} data-reveal data-phase={key}>
                <span className={styles.phaseLabel}>{t(`phases.items.${key}.label`)}</span>
                <h3>{t(`phases.items.${key}.title`)}</h3>
                <p>{t(`phases.items.${key}.body`)}</p>
                {key === "phase1" ? (
                  <span className={styles.phaseWindow}>{t(`phases.items.${key}.window`)}</span>
                ) : null}
              </article>
            ))}
          </Reveal>
        </Container>
      </section>

      <section className={`${shared.section} ${shared.sunken}`}>
        <Container>
          <SectionHeading title={t("objectives.title")} />
          <Reveal className={`${styles.objectiveGrid} ${shared.stackGap}`}>
            {objectives.map((item, index) => (
              <article key={item.title} className={styles.objective} data-reveal>
                <span className={styles.number}>{String(index + 1).padStart(2, "0")}</span>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </Reveal>
        </Container>
      </section>

      <section className={`${shared.section} ${shared.atlas}`}>
        <Container>
          <SectionHeading title={t("system.title")} lead={t("system.lead")} />
          <p className={styles.workflow} data-reveal>
            {t("system.workflow")}
          </p>
          <Reveal className={`${styles.systemGrid} ${shared.stackGap}`}>
            {systemItems.map((item) => (
              <article key={item.title} className={styles.systemCard} data-reveal>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </Reveal>
        </Container>
      </section>

      <section className={shared.section}>
        <Container>
          <Reveal className={shared.asymmetric}>
            <div>
              <SectionHeading title={t("scope.title")} lead={t("scope.body")} />
            </div>
            <div>
              <SectionHeading title={t("outOfScope.title")} />
              <ul className={styles.outList}>
                {outOfScope.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </Container>
      </section>

      <section className={`${shared.section} ${shared.fieldbook}`}>
        <Container>
          <SectionHeading title={t("principles.title")} />
          <Reveal className={`${styles.principles} ${shared.stackGap}`}>
            {principles.map((item, index) => (
              <article key={item.title} className={styles.principle} data-reveal>
                <span className={styles.number}>{String(index + 1).padStart(2, "0")}</span>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </Reveal>
        </Container>
      </section>

      <section className={`${shared.section} ${shared.atlas}`}>
        <Container>
          <SectionHeading title={t("timeline.title")} />
          <Reveal className={shared.stackGap}>
            <MilestoneRail items={milestones} />
          </Reveal>
        </Container>
      </section>

      <section className={shared.section}>
        <Container>
          <Reveal className={styles.disclaimer}>
            <div>
              <span className="eyebrow" data-reveal>
                {t("disclaimer.title")}
              </span>
              <p data-reveal>{t("disclaimer.body")}</p>
            </div>
            <div className={styles.ctaButtons} data-reveal>
              <Button to={lp(ROUTES.SCIENCE)} variant="primary">
                {t("cta.primary")}
              </Button>
              <Button to={lp(ROUTES.TEAM)} variant="secondary">
                {t("cta.secondary")}
              </Button>
            </div>
          </Reveal>
        </Container>
      </section>
    </>
  );
});

AboutPage.displayName = "AboutPage";
export default AboutPage;
